import React, { useState, useEffect } from "react";
import CardView from "../../components/molecules/CardView/CardView";
import DataTable from "../../components/organisms/DataTable/DataTable";
import Input from "../../components/atoms/Input/Input";
import ToggleTokens from "../../components/atoms/ToggleTokens/ToggleTokens";
import "./BusinessPayments.scss";

const BusinessPayments = () => {
    const [searchTerm, setSearchTerm] = useState("");
    // const [loading, setLoading] = useState(false);
    const [filteredData, setFilteredData] = useState([]);

    // Datos de ejemplo (esto vendría de una API)
    const businessData = [
        {
            id: 1,
            business_name: "Tech Innovators",
            owner_name: "Carlos López",
            creation_date: "2022-01-15",
            membership: "Premium",
            days_remaining: 10,
            last_payment: "2024-07-15",
        },
        {
            id: 2,
            business_name: "Green Earth",
            owner_name: "Laura Martínez",
            creation_date: "2021-05-22",
            membership: "Basic",
            days_remaining: 13,
            last_payment: "2024-06-20",
        },
        {
            id: 3,
            business_name: "Digital Solutions",
            owner_name: "Andrés Fernández",
            creation_date: "2020-11-10",
            membership: "Pro",
            days_remaining: 3,
            last_payment: "2024-08-01",
        },
        {
            id: 4,
            business_name: "Healthy Life",
            owner_name: "María García",
            creation_date: "2019-03-30",
            membership: "Basic",
            days_remaining: 1,
            last_payment: "2024-07-25",
        },
        {
            id: 5,
            business_name: "Tech World",
            owner_name: "Pedro Sánchez",
            creation_date: "2018-08-15",
            membership: "Pro",
            days_remaining: 6,
            last_payment: "2024-07-10",
        },
    ];

    // Filtros de tiempo (por días restantes)
    const [filters, setFilters] = useState([
        {
            id: 0,
            text: "Todos",
            state: true,
            color: "purple",
            filter: null,
        },
        {
            id: 1,
            text: "Críticos (≤1)",
            state: false,
            color: "red",
            filter: (days) => days <= 1,
        },
        {
            id: 2,
            text: "Urgentes (2-5)",
            state: false,
            color: "orange",
            filter: (days) => days >= 2 && days <= 5,
        },
        {
            id: 3,
            text: "Próximos (6-10)",
            state: false,
            color: "yellow",
            filter: (days) => days >= 6 && days <= 10,
        },
        {
            id: 4,
            text: "Estables (>10)",
            state: false,
            color: "green",
            filter: (days) => days > 10,
        },
    ]);

    // Componente para mostrar el badge de días restantes
    const DaysBadge = ({ days }) => {
        const getColor = () => {
            if (days <= 1) return { bg: "#ffb3ba", text: "#8b0000" }; // Rojo pastel
            if (days <= 5) return { bg: "#ffd4a3", text: "#8b4513" }; // Naranja pastel
            if (days <= 10) return { bg: "#fff4a3", text: "#856404" }; // Amarillo pastel
            return { bg: "#bae1c8", text: "#155724" }; // Verde pastel
        };

        const colors = getColor();

        return (
            <span
                className="business-payments__badge"
                style={{
                    backgroundColor: colors.bg,
                    color: colors.text,
                }}
            >
                {days} {days === 1 ? "día" : "días"}
            </span>
        );
    };

    // Definir las columnas de la tabla
    const columns = [
        {
            field: "business_name",
            header: "Nombre del negocio",
            sortable: true,
        },
        {
            field: "owner_name",
            header: "Propietario",
            sortable: true,
        },
        {
            field: "creation_date",
            header: "Fecha de creación",
            sortable: true,
            body: (rowData) => {
                return new Date(rowData.creation_date).toLocaleDateString(
                    "es-ES"
                );
            },
        },
        {
            field: "membership",
            header: "Membresía",
            sortable: true,
        },
        {
            field: "days_remaining",
            header: "Status (Días)",
            sortable: true,
            body: (rowData) => {
                return <DaysBadge days={rowData.days_remaining} />;
            },
        },
        {
            field: "last_payment",
            header: "Último pago",
            sortable: true,
            body: (rowData) => {
                return new Date(rowData.last_payment).toLocaleDateString(
                    "es-ES"
                );
            },
        },
    ];

    // Manejar cambio de filtro
    const handleToggleChange = (id) => {
        setFilters((prevFilters) =>
            prevFilters.map((filter) =>
                filter.id === id ? { ...filter, state: !filter.state } : filter
            )
        );
    };

    // Aplicar filtros
    useEffect(() => {
        let result = businessData;

        // Filtro por búsqueda
        if (searchTerm.trim()) {
            const search = searchTerm.toLowerCase();
            result = result.filter(
                (item) =>
                    item.business_name.toLowerCase().includes(search) ||
                    item.owner_name.toLowerCase().includes(search)
            );
        }

        // Filtro por días restantes
        const activeFilters = filters.filter((f) => f.state && f.filter);

        if (activeFilters.length > 0) {
            result = result.filter((item) =>
                activeFilters.some((f) => f.filter(item.days_remaining))
            );
        }

        setFilteredData(result);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, filters]);

    const handleSearch = (e) => {
        e.preventDefault();
        // La búsqueda se maneja automáticamente con useEffect
    };

    const handleRowClick = (rowData) => {
        // Aquí puedes navegar a una vista de detalles
    };

    return (
        <CardView>
            <div className="business-payments">
                <div className="business-payments__header">
                    <h1 className="business-payments__title">
                        Pagos de Negocios
                    </h1>
                </div>

                <div className="business-payments__filters">
                    <div className="business-payments__filters-section">
                        <p className="business-payments__filters-label">
                            Filtrar por días restantes de membresía
                        </p>
                        <div className="business-payments__toggles">
                            {filters.map((filter) => (
                                <ToggleTokens
                                    key={filter.id}
                                    id={filter.id}
                                    text={filter.text}
                                    isChecked={filter.state}
                                    class_toggle={filter.color}
                                    setIsChecked={() =>
                                        handleToggleChange(filter.id)
                                    }
                                />
                            ))}
                        </div>
                    </div>

                    <form
                        onSubmit={handleSearch}
                        className="business-payments__search-form"
                    >
                        <Input
                            placeholder="Buscar negocio o propietario..."
                            type="search"
                            className="business-payments__search-input"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <button
                            type="submit"
                            className="business-payments__search-btn"
                        >
                            Buscar
                        </button>
                    </form>
                </div>

                <DataTable
                    data={filteredData}
                    columns={columns}
                    // loading={loading}
                    onRowClick={handleRowClick}
                    emptyMessage="No hay negocios registrados"
                    striped={true}
                    hoverable={true}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    responsiveLayout="scroll"
                />
            </div>
        </CardView>
    );
};

export default BusinessPayments;
